import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { FormTextInput, Loader } from '@liveconnect/components'

import useConfigurationFooter from '../../../../core/configuration/footer/useFooter'
import useNotifications from '../../../../utils/notifications/useNotifications'
import { DEFAULT_LANG } from '../../../../i18n/config'

import CustomForm from '../../../../components/CustomForm'
import TranslationCard from '../../../../components/TranslationCard'
import { ConfigurationFooterPages } from '../Shared/Pages'

import {
  ConfigurationFooterContactInfoForm,
  UrlTypeFooterPage,
} from '../../../../core/configuration/footer/types'

import { buildValidationSchema } from './validations'

const FooterContactInfo = () => {
  const { t } = useTranslation()
  const notify = useNotifications()
  const {
    isLoaded,
    contactInfo: { list },
    fetchContactInfoList,
    createContactInfo,
    updateContactInfo,
    translateContactInfo,
  } = useConfigurationFooter()

  const isContactInfoSetted = useMemo(() => {
    if (!list) return false
    return list.id !== '00000000-0000-0000-0000-000000000000'
  }, [isLoaded, list])

  const defaultValues = {
    language: DEFAULT_LANG,
    footerId: isContactInfoSetted ? list?.footerId : undefined,
  }

  const methods = useForm<ConfigurationFooterContactInfoForm>({
    mode: 'onChange',
    resolver: yupResolver(buildValidationSchema(t)),
    defaultValues,
  })

  const { control, watch, reset } = methods

  const formValues = watch()

  const onSubmit = async (values: ConfigurationFooterContactInfoForm) => {
    const isTranslating = formValues.language !== DEFAULT_LANG
    let action = 'create'
    if (isContactInfoSetted) action = 'edit'
    if (isTranslating) action = 'translate'
    try {
      if (!isContactInfoSetted) {
        await createContactInfo(values)
        fetchContactInfoList()
      } else {
        if (isTranslating) {
          await translateContactInfo(list!.id, values)
        } else {
          await updateContactInfo(list!.id, values)
        }
      }
      reset(formValues)
      notify.success(t(`footerInfo.contactInfo.feedback.${action}.ok`))
    } catch (error) {
      notify.error(t(`footerInfo.contactInfo.feedback.${action}.ko`))
    }
  }

  useEffect(() => {
    fetchContactInfoList()
  }, [])

  useEffect(() => {
    if (list && isContactInfoSetted) {
      const translation = list.contactInfoTranslations.find(
        (ct) => ct.language === formValues.language
      )
      reset({
        ...formValues,
        sectionTitle: translation ? translation.sectionTitle : '',
      })
    } else {
      reset(defaultValues)
    }
  }, [list, formValues.language])

  if (!isLoaded) {
    return <Loader />
  }

  return (
    <CustomForm
      methods={methods}
      onSubmit={onSubmit}
      onCancel={() => reset(defaultValues)}
      isSelectDisabled={!isContactInfoSetted}
    >
      {isContactInfoSetted && list && (
        <TranslationCard
          translations={list.translations.map(
            (t) =>
              ({
                language: t,
                name: t,
              } as any)
          )}
          lang={formValues.language}
          entity={t('footerInfo.contactInfo.entity')}
        />
      )}
      <div className="row mb-5">
        <div className="col-9">
          <FormTextInput
            label={t('footerInfo.contactInfo.form.sectionTitle.label')}
            placeholder={t(
              'footerInfo.contactInfo.form.sectionTitle.placeholder'
            )}
            control={control}
            name="sectionTitle"
            required
            data-testid="footerInfo-contactInfo-sectionTitle"
            helperText={t('validations.leyendField.max', { number: 50 })}
          />
        </div>
      </div>
      {isContactInfoSetted && list && (
        <ConfigurationFooterPages
          title={t('footerInfo.contactInfo.table.title')}
          type={UrlTypeFooterPage.ContactInfo}
          tabId={list.id}
          data={list.pages}
        />
      )}
    </CustomForm>
  )
}

export default FooterContactInfo
